import React, { useContext, useEffect, useMemo, useState } from 'react';

import { CombatContext } from '../contexts/CombatContext';
import {
  ActionPill, StatusBadge, WorkspaceCard, useLayeredModal
} from './ui/LayeredWorkspace';
import { buildPlayerSafeCombatProjection, DEFAULT_PLAYER_VIEW_SETTINGS } from './playerVisibilityEngine';
import PlayerSpellsChip from './PlayerSpellsWorkspace';
import PlayerInventoryChip from './PlayerInventoryWorkspace';
import {
  correctInformationCard, ensureSharingContext, fetchSharingPreview,
  revokeInformationCard, shareInformationCard,
} from './sharingApi';
import { runtimeContextPayload } from './combatContextIdentity';

const VISIBILITIES = [
  'DM_ONLY', 'SHAREABLE', 'PARTY', 'SELECTED_PLAYERS', 'SINGLE_PLAYER', 'PUBLIC_COMBAT_STATE'
];
const GRANULARITIES = [
  'WHOLE_RESULT', 'WHOLE_CARD', 'SECTION', 'FACT', 'DISCOVERY', 'ITEM', 'OBJECTIVE_HAZARD_EFFECT'
];

function ProjectionPanel({ projection }) {
  return (
    <div className="cp-player-preview-stage" aria-label="Exact player-safe combat preview">
      <div className="cp-player-preview-head"><strong>{projection.encounter}</strong><span>Round {projection.round}</span></div>
      {projection.objective ? <div className="cp-player-preview-objective"><b>Objective</b> {projection.objective}</div> : null}
      <div className="cp-player-preview-columns">
        <div><h3>Party</h3>{projection.party.map(function (row) { return <div className="cp-player-preview-row" key={row.key}><b>{row.name}</b><span>{row.status}</span><small>{row.conditions.join(', ')}</small></div>; })}</div>
        <div><h3>Enemies</h3>{projection.enemies.map(function (row) { return <div className={'cp-player-preview-row' + (row.current ? ' is-current' : '')} key={row.key}><b>{row.name}</b><span>{row.status}</span><small>{row.conditions.join(', ')}</small></div>; })}</div>
      </div>
    </div>
  );
}

export function PlayerVisibilityWorkspace() {
  const { state } = useContext(CombatContext);
  const [settings, setSettings] = useState({ ...DEFAULT_PLAYER_VIEW_SETTINGS });
  const [context, setContext] = useState(null);
  const [selectedRuntimeIds, setSelectedRuntimeIds] = useState(function () { return (state.players || []).filter(function (p) { return !p.companion; }).map(function (p) { return String(p.id); }); });
  const [preview, setPreview] = useState({ players: [] });
  const [visibility, setVisibility] = useState('PARTY');
  const [granularity, setGranularity] = useState('FACT');
  const [title, setTitle] = useState('');
  const [fact, setFact] = useState('');
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState('Connecting secure sharing…');
  const projection = useMemo(function () { return buildPlayerSafeCombatProjection(state, settings); }, [state, settings]);

  async function refresh(nextContext, runtimeSelection) {
    const ctx = nextContext || context;
    if (!ctx) return;
    const selected = runtimeSelection || selectedRuntimeIds;
    const ids = (ctx.players || []).filter(function (player) { return selected.indexOf(String(player.runtime_ref)) >= 0; })
      .map(function (player) { return player.player_id; });
    if (!ids.length) {
      setPreview({ campaign_id: ctx.campaign_id, players: [] });
      return;
    }
    const data = await fetchSharingPreview(ctx.campaign_id, ids.length === (ctx.players || []).length ? [] : ids);
    setPreview(data);
  }

  useEffect(function () {
    let live = true;
    ensureSharingContext(runtimeContextPayload(state)).then(function (data) {
      if (!live) return;
      setContext(data);
      setNotice('Player-safe projection connected.');
      return refresh(data, selectedRuntimeIds);
    }).catch(function () {
      if (live) setNotice('Live combat preview is available; persistent sharing could not connect.');
    });
    return function () { live = false; };
    // The context adapter runs once per modal opening; live combat projection itself remains reactive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleSetting(key) {
    setSettings(function (current) { return { ...current, [key]: !current[key] }; });
  }

  function togglePlayer(runtimeRef) {
    const next = selectedRuntimeIds.indexOf(runtimeRef) >= 0
      ? selectedRuntimeIds.filter(function (id) { return id !== runtimeRef; })
      : selectedRuntimeIds.concat([runtimeRef]);
    setSelectedRuntimeIds(next);
    if (context) refresh(context, next).catch(function () { setNotice('Preview refresh failed.'); });
  }

  async function submitShare() {
    if (!context || !title.trim() || !fact.trim()) return;
    let recipientIds = context.players.filter(function (player) { return selectedRuntimeIds.indexOf(String(player.runtime_ref)) >= 0; })
      .map(function (player) { return player.player_id; });
    if (visibility === 'SINGLE_PLAYER') recipientIds = recipientIds.slice(0, 1);
    if (visibility === 'PARTY' || visibility === 'PUBLIC_COMBAT_STATE' || visibility === 'DM_ONLY' || visibility === 'SHAREABLE') recipientIds = [];
    const payload = {
      campaign_id: context.campaign_id,
      title: title.trim(), card_type: granularity === 'DISCOVERY' ? 'DISCOVERY' : 'INFORMATION',
      source_kind: 'COMBAT_PRO', granularity: granularity, visibility: visibility,
      recipient_player_ids: recipientIds,
      player_projection: {
        display_cards: [{ title: title.trim(), summary: fact.trim() }],
        player_safe_facts: [fact.trim()],
      },
      dm_projection: {},
    };
    try {
      if (editing) {
        const correction = { ...payload, expected_version: editing.version };
        delete correction.campaign_id;
        await correctInformationCard(editing.id, correction);
      } else {
        await shareInformationCard(payload);
      }
      setEditing(null); setTitle(''); setFact(''); setNotice(editing ? 'Correction recorded.' : 'Information shared.');
      await refresh(context, selectedRuntimeIds);
    } catch (error) {
      setNotice('Share was rejected. Check the audience and try again.');
    }
  }

  async function revoke(card) {
    try {
      await revokeInformationCard(card.id, card.version);
      setNotice('Future visibility revoked; audit history retained.');
      await refresh(context, selectedRuntimeIds);
    } catch (error) {
      setNotice('Revoke was rejected because the card changed. Refresh and try again.');
    }
  }

  const previewById = {};
  (preview.players || []).forEach(function (player) {
    (player.cards || []).forEach(function (card) {
      if (!previewById[card.id]) previewById[card.id] = { ...card, audiences: [] };
      previewById[card.id].audiences.push(player.display_name);
    });
  });
  const previewCards = Object.keys(previewById).map(function (id) { return previewById[id]; });

  return (
    <div className="cp-player-visibility">
      <div className="cp-visibility-toolbar">
        <div><span className="cp-layer-eyebrow">Preview audience</span><div className="cp-action-row">
          <ActionPill primary={Boolean(context && selectedRuntimeIds.length === context.players.length)} onClick={function () { const all=(state.players || []).filter(function (p) { return !p.companion; }).map(function (p) { return String(p.id); }); setSelectedRuntimeIds(all); if (context) refresh(context, all).catch(function () { setNotice('Preview refresh failed.'); }); }}>All Players</ActionPill>
          {(state.players || []).filter(function (p) { return !p.companion; }).map(function (player) { const id=String(player.id); return <ActionPill key={id} primary={selectedRuntimeIds.indexOf(id) >= 0} onClick={function () { togglePlayer(id); }}>{player.name}</ActionPill>; })}
        </div></div>
        <StatusBadge tone={context ? 'ready' : 'pending'}>{context ? 'READY' : 'PENDING'}</StatusBadge>
      </div>
      <p className="cp-visibility-notice" role="status">{notice}</p>
      <div className="cp-visibility-settings" role="group" aria-label="Public combat fields">
        {Object.keys(DEFAULT_PLAYER_VIEW_SETTINGS).map(function (key) { return <label key={key}><input type="checkbox" checked={settings[key]} onChange={function () { toggleSetting(key); }} /> {key.replace(/([A-Z])/g, ' $1').replace(/^show /, '')}</label>; })}
      </div>
      <div className="cp-player-control-chips" aria-label="Player controls">
        {(state.players || []).filter(function (player) { return !player.companion; }).map(function (player) {
          return <React.Fragment key={player.id}><PlayerSpellsChip playerId={player.id} label={player.name + ' Spells'} /><PlayerInventoryChip playerId={player.id} label={player.name + ' Inventory'} /></React.Fragment>;
        })}
      </div>
      <ProjectionPanel projection={projection} />

      <section className="cp-share-compose" aria-label="Share player-safe information">
        <h3>{editing ? 'Correct shared card' : 'Share information'}</h3>
        <div className="cp-share-grid">
          <label>Title<input value={title} maxLength="240" onChange={function (event) { setTitle(event.target.value); }} /></label>
          <label>Player-safe fact<textarea value={fact} onChange={function (event) { setFact(event.target.value); }} /></label>
          <label>Granularity<select value={granularity} onChange={function (event) { setGranularity(event.target.value); }}>{GRANULARITIES.map(function (value) { return <option key={value}>{value}</option>; })}</select></label>
          <label>Audience<select value={visibility} onChange={function (event) { setVisibility(event.target.value); }}>{VISIBILITIES.map(function (value) { return <option key={value}>{value}</option>; })}</select></label>
        </div>
        <div className="cp-action-row"><ActionPill primary onClick={submitShare}>{editing ? 'Record Correction' : 'Share'}</ActionPill>{editing ? <ActionPill onClick={function () { setEditing(null); setTitle(''); setFact(''); }}>Cancel</ActionPill> : null}</div>
      </section>

      <section aria-label="Exact shared-card preview"><h3>Shared cards players receive</h3>
        <div className="cp-workspace-grid">{previewCards.length ? previewCards.map(function (card, index) {
          const facts = card.projection && card.projection.player_safe_facts ? card.projection.player_safe_facts : [];
          return <div key={card.id + ':' + index} className="cp-shared-preview-card"><WorkspaceCard title={card.title} type={card.type} source={card.source} status={card.status} summary={facts[0] || 'Shared player-safe card'} meta={'Recipient: ' + card.audiences.join(', ')} /><div className="cp-action-row"><ActionPill onClick={function () { setEditing(card); setTitle(card.title); setFact(facts[0] || ''); setVisibility(card.visibility); setGranularity(card.granularity); }}>Correct</ActionPill><ActionPill onClick={function () { revoke(card); }}>Revoke</ActionPill></div></div>;
        }) : <p>No authorised shared cards for the selected audience.</p>}</div>
      </section>
    </div>
  );
}

export default function PlayerVisibilityChip() {
  const { openLayer } = useLayeredModal();
  return <button type="button" className="playerview-toggle cp-workspace-chip" onClick={function () { openLayer({ title: 'Player View', eyebrow: 'DM Preview', subtitle: 'The exact player-safe projection and controlled sharing workspace.', content: <PlayerVisibilityWorkspace /> }); }}>Player View</button>;
}
